import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import {
  C,
  fmtTokens as fmtTokensDash,
  fmtEuros,
  fmtLatencia,
  getPeriodLabel,
  DashKPICard,
  DashContentCard,
  DashSparkline,
  DashMiniBar,
  DashHeatmap,
  DashPeriodFilter,
} from '../../../shared/components/dashboard';
import { useI18n } from '../../../../i18n';
import type { DashboardData } from './admin-ia-shared';

interface AdminIADashboardProps {
  dashData: DashboardData;
  dashLoading: boolean;
  dashFiltering: boolean;
  dashPeriodo: string;
  onPeriodoChange: (p: string) => void;
}

export function AdminIADashboard({
  dashData,
  dashLoading,
  dashFiltering,
  dashPeriodo,
  onPeriodoChange,
}: AdminIADashboardProps) {
  const { t } = useI18n();

  if (dashLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner text={t.admin.via.cargando} />
      </div>
    );
  }

  const d = dashData;
  const pctUsado =
    d.tokensLimite > 0 ? Math.round((d.tokensUsados / d.tokensLimite) * 1000) / 10 : 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        position: 'relative',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${C.grisMedio}`,
          paddingBottom: 10,
          marginBottom: 20,
        }}
      >
        <h2
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: C.azul1,
            letterSpacing: -0.3,
            margin: 0,
          }}
        >
          Inteligencia artificial &middot; Cuadro de mando
        </h2>
        <span style={{ fontSize: 12, color: C.grisOscuro }}>{getPeriodLabel(dashPeriodo)}</span>
      </div>

      {/* Period pill buttons */}
      <DashPeriodFilter value={dashPeriodo} onChange={onPeriodoChange} />

      {/* Loading overlay */}
      <div
        style={{
          opacity: dashFiltering ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
          position: 'relative',
        }}
      >
        {dashFiltering && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              fontSize: 10,
              color: '#aaa',
              fontStyle: 'italic',
              zIndex: 10,
            }}
          >
            Actualizando...
          </span>
        )}

        {/* ROW 1: 6 KPIs */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 10,
            marginBottom: 12,
          }}
        >
          {[
            { val: String(d.interacciones), label: 'Interacciones', sub: 'este mes' },
            {
              val: fmtTokensDash(d.tokensUsados),
              label: 'Tokens consumidos',
              sub: `de 1M incluidos (${pctUsado}%)`,
            },
            {
              val: fmtEuros(d.costeTotalEstimado),
              label: 'Coste acumulado',
              sub: 'este mes',
            },
            {
              val: fmtLatencia(d.latenciaMedia),
              label: 'Latencia media',
              sub: 'tiempo de respuesta',
            },
            {
              val: `${d.tasaExito}%`,
              label: 'Tasa de exito',
              sub: 'sin fallback necesario',
            },
            {
              val: `${d.similitudMedia}%`,
              label: 'Calidad RAG',
              sub: `${d.chunksMediaConsulta} chunks/consulta`,
            },
          ].map((k, i) => (
            <DashKPICard key={i} value={k.val} label={k.label} subtitle={k.sub} />
          ))}
        </div>

        {/* ROW 2: 4 cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10,
            marginBottom: 12,
            minHeight: 260,
          }}
        >
          {/* Card 1: Consumo por modo */}
          <DashContentCard title="consumo por modo">
            {d.consumoPorModo.length > 0 && (
              <DashMiniBar
                data={d.consumoPorModo.map((m) => ({
                  label: m.modo,
                  value: m.tokens,
                }))}
                maxVal={d.tokensLimite}
              />
            )}
            <div
              style={{
                height: 3,
                background: C.grisClaro,
                borderRadius: 2,
                overflow: 'hidden',
                margin: '10px 0 4px',
              }}
            >
              <div
                style={{
                  height: '100%',
                  borderRadius: 2,
                  background: C.azul1,
                  width: `${Math.min(pctUsado, 100)}%`,
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 10,
                color: '#aaa',
              }}
            >
              <span>{fmtTokensDash(d.tokensUsados)} usados</span>
              <span>{fmtTokensDash(d.tokensLimite)} limite</span>
            </div>
            <div style={{ marginTop: 'auto' }} />
          </DashContentCard>

          {/* Card 2: Proveedores de IA */}
          <DashContentCard title="proveedores de IA">
            {d.proveedores
              .filter((p) => p.llamadas > 0 || p.rol === 'Principal' || p.rol === 'Fallback')
              .map((p, i, arr) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 0',
                    borderBottom: i < arr.length - 1 ? `0.5px solid ${C.grisClaro}` : 'none',
                    borderRadius: 4,
                    paddingLeft: 4,
                    paddingRight: 4,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background:
                        p.estado === 'activo'
                          ? C.azul2
                          : p.estado === 'standby'
                            ? C.grisOscuro
                            : C.grisMedio,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{p.nombre}</div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>
                      {p.modelo} &middot; {p.rol}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14 }}>
                    <div style={{ textAlign: 'right' as const }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                        {p.llamadas}
                      </div>
                      <div
                        style={{
                          fontSize: 8,
                          color: '#aaa',
                          textTransform: 'uppercase',
                          letterSpacing: 0.3,
                        }}
                      >
                        llamadas
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' as const }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                        {fmtEuros(p.coste)}
                      </div>
                      <div
                        style={{
                          fontSize: 8,
                          color: '#aaa',
                          textTransform: 'uppercase',
                          letterSpacing: 0.3,
                        }}
                      >
                        coste
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {d.proveedores.filter(
              (p) => p.llamadas > 0 || p.rol === 'Principal' || p.rol === 'Fallback',
            ).length === 0 && (
              <div style={{ fontSize: 12, color: '#aaa', padding: '8px 0' }}>
                Sin proveedores configurados
              </div>
            )}
            <div style={{ marginTop: 'auto' }} />
          </DashContentCard>

          {/* Card 3: Coste y facturacion */}
          <DashContentCard title="coste y facturacion">
            <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 27, fontWeight: 700, color: C.azul1 }}>
                  {fmtEuros(d.costeTotalEstimado)}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: '#aaa',
                    textTransform: 'uppercase',
                    marginTop: 2,
                  }}
                >
                  Coste este mes
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 27, fontWeight: 700, color: C.azul1 }}>{fmtEuros(0)}</div>
                <div
                  style={{
                    fontSize: 9,
                    color: '#aaa',
                    textTransform: 'uppercase',
                    marginTop: 2,
                  }}
                >
                  Proyeccion exceso
                </div>
              </div>
            </div>
            <div style={{ borderTop: `0.5px solid ${C.grisClaro}`, paddingTop: 8 }}>
              {d.proveedores
                .filter((p) => p.llamadas > 0)
                .map((p, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: `0.5px solid ${C.grisClaro}`,
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: C.grisOscuro }}>
                      {p.nombre}{' '}
                      <span style={{ fontSize: 10, color: '#aaa' }}>({p.llamadas} llamadas)</span>
                    </span>
                    <span style={{ fontWeight: 600, color: '#333' }}>{fmtEuros(p.coste)}</span>
                  </div>
                ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  fontSize: 12,
                }}
              >
                <span style={{ color: C.grisOscuro }}>Tokens incluidos en plan</span>
                <span style={{ fontWeight: 600, color: '#333' }}>
                  {fmtTokensDash(d.tokensLimite)}/mes
                </span>
              </div>
            </div>
            {d.proveedores.find((p) => p.creditos) && (
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  background: C.grisClaro,
                  borderRadius: 6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                }}
              >
                <span style={{ color: C.grisOscuro }}>Creditos Claude restantes</span>
                <span style={{ fontWeight: 700, color: C.azul1 }}>
                  {fmtEuros(d.proveedores.find((p) => p.creditos)?.creditos || 0)}
                </span>
              </div>
            )}
            <div style={{ marginTop: 'auto' }} />
          </DashContentCard>

          {/* Card 4: Base de conocimiento RAG */}
          <DashContentCard title="base de conocimiento RAG">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 0,
                marginBottom: 12,
              }}
            >
              {[
                { val: d.docsIndexados, label: 'Documentos' },
                { val: d.chunksTotal, label: 'Chunks' },
                { val: d.dimensiones, label: 'Dimensiones' },
              ].map((r, i) => (
                <div key={i} style={{ textAlign: 'center', padding: '6px 0' }}>
                  <div style={{ fontSize: 21, fontWeight: 700, color: C.azul1 }}>{r.val}</div>
                  <div
                    style={{
                      fontSize: 9,
                      color: C.grisOscuro,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginTop: 2,
                    }}
                  >
                    {r.label}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: `0.5px solid ${C.grisClaro}`, paddingTop: 8 }}>
              {[
                { label: 'Modelo embeddings', value: 'Gemini Embedding' },
                { label: 'Fallback', value: 'BGE-M3 (HuggingFace)' },
                { label: 'Chunks/consulta', value: String(d.chunksMediaConsulta) },
                { label: 'Similitud media', value: `${d.similitudMedia}%` },
                { label: 'Cobertura', value: '100%' },
              ].map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    padding: '3px 0',
                  }}
                >
                  <span style={{ color: C.grisOscuro }}>{r.label}</span>
                  <span style={{ fontWeight: 500, color: '#333' }}>{r.value}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 'auto' }} />
          </DashContentCard>
        </div>

        {/* ROW 3: 2 wide cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            marginBottom: 12,
            minHeight: 260,
          }}
        >
          {/* Card 1: Actividad diaria */}
          <DashContentCard title="actividad diaria">
            {d.actividadDiaria.length > 0 ? (
              <>
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 10, color: '#aaa', marginBottom: 4 }}>Interacciones</div>
                  <DashSparkline
                    points={d.actividadDiaria.map((a) => a.interacciones)}
                    color={C.azul1}
                    height={36}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#aaa', marginBottom: 4 }}>
                    Tokens medios (K)
                  </div>
                  <DashSparkline
                    points={d.actividadDiaria.map((a) => a.tokensMedio)}
                    color={C.azulClaro}
                    height={36}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 9,
                    color: '#bbb',
                    marginTop: 6,
                  }}
                >
                  {d.actividadDiaria.map((a, i) => (
                    <span key={i}>{a.dia}</span>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#aaa', padding: '8px 0' }}>
                Sin datos de actividad
              </div>
            )}
          </DashContentCard>

          {/* Card 2: Heatmap semanal */}
          <DashContentCard title="distribucion semanal">
            <DashHeatmap data={d.heatmap} />
            {d.heatmap.length > 0 && (
              <div
                style={{
                  borderTop: `0.5px solid ${C.grisClaro}`,
                  paddingTop: 8,
                  marginTop: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                }}
              >
                <span style={{ color: C.grisOscuro }}>Pico de actividad</span>
                <span style={{ fontWeight: 500, color: '#333' }}>Miercoles y jueves</span>
              </div>
            )}
          </DashContentCard>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          fontSize: 9,
          color: '#ccc',
          textAlign: 'right',
          marginTop: 16,
          fontStyle: 'italic',
        }}
      >
        Datos de registros_auditoria y consumo_ia &middot; VALOR&middot;IA Governance &middot;
        actualizado {new Date().toLocaleDateString('es-ES')}
      </div>
    </div>
  );
}
